import type {
    ContentStateValue,
    ContentStateData,
    EngineEvent,
    EventListener,
    EventUnsubscribe,
} from './types';
import { EventEmitter, createEvent } from './events';

/**
 * Default state data
 */
export function getDefaultStateData<TAnswer = unknown>(): ContentStateData<TAnswer> {
    return {
        state: 'idle',
        selectedAnswer: null,
        showFeedback: false,
        showHint: false,
        showExplanation: false,
        isLocked: false,
        isCorrect: null,
        score: 0,
        max: 1,
        feedback: undefined,
    };
}

/**
 * StateManager - Manages content interaction state
 */
export class StateManager<TAnswer = unknown> {
    private contentId: string;
    private state: ContentStateData<TAnswer>;
    private emitter: EventEmitter = new EventEmitter();

    constructor(contentId: string, initialState?: Partial<ContentStateData<TAnswer>>) {
        this.contentId = contentId;
        this.state = { ...getDefaultStateData<TAnswer>(), ...initialState };
    }

    /**
     * Get current state data
     */
    getState(): ContentStateData<TAnswer> {
        return { ...this.state };
    }

    /**
     * Get content ID
     */
    getContentId(): string {
        return this.contentId;
    }

    /**
     * Get current state value
     */
    getStateValue(): ContentStateValue {
        return this.state.state;
    }

    /**
     * Set state value
     */
    setStateValue(value: ContentStateValue): void {
        const previousState = this.state.state;
        if (previousState === value) return;

        this.state.state = value;

        this.emit(
            createEvent('state:change', this.contentId, {
                previousState,
                newState: value,
            })
        );
    }

    /**
     * Set selected answer
     */
    setAnswer(answer: TAnswer | null): void {
        this.state.selectedAnswer = answer;

        if (this.state.state === 'idle') {
            this.state.state = 'answering';
        }

        this.emit(createEvent('answer:change', this.contentId, { answer }));
    }

    /**
     * Get selected answer
     */
    getAnswer(): TAnswer | null {
        return this.state.selectedAnswer;
    }

    /**
     * Set result after grading
     */
    setResult(isCorrect: boolean, score: number, max: number, feedback?: string): void {
        this.state.isCorrect = isCorrect;
        this.state.score = score;
        this.state.max = max;
        this.state.feedback = feedback;

        this.emit(
            createEvent('score:update', this.contentId, {
                isCorrect,
                score,
                max,
                feedback,
            })
        );
    }

    /**
     * Show/hide feedback
     */
    setShowFeedback(show: boolean): void {
        if (this.state.showFeedback === show) return;
        this.state.showFeedback = show;
        this.emit(createEvent(show ? 'feedback:show' : 'feedback:hide', this.contentId));
    }

    /**
     * Show/hide hint
     */
    setShowHint(show: boolean): void {
        this.state.showHint = show;
    }

    /**
     * Show/hide explanation
     */
    setShowExplanation(show: boolean): void {
        this.state.showExplanation = show;
    }

    /**
     * Lock/unlock content
     */
    setLocked(locked: boolean): void {
        this.state.isLocked = locked;
    }

    /**
     * Check if content is interactive
     */
    isInteractive(): boolean {
        return (
            !this.state.isLocked &&
            (this.state.state === 'idle' ||
                this.state.state === 'active' ||
                this.state.state === 'answering')
        );
    }

    /**
     * Check if answer is selected
     */
    hasAnswer(): boolean {
        return this.state.selectedAnswer !== null;
    }

    /**
     * Activate content
     */
    activate(): void {
        if (this.state.state === 'idle') {
            this.setStateValue('active');
        }
    }

    /**
     * Mark as submitted
     */
    submit(): void {
        this.setStateValue('submitted');
        this.state.showFeedback = true;
    }

    /**
     * Mark as completed
     */
    complete(): void {
        this.setStateValue('completed');
        this.state.isLocked = true;
    }

    /**
     * Enter review mode
     */
    review(): void {
        this.setStateValue('reviewing');
        this.state.showFeedback = true;
        this.state.showExplanation = true;
        this.state.isLocked = true;
    }

    /**
     * Pause content
     */
    pause(): void {
        if (this.state.state !== 'paused') {
            this.setStateValue('paused');
        }
    }

    /**
     * Resume content
     */
    resume(): void {
        if (this.state.state === 'paused') {
            this.setStateValue('active');
        }
    }

    /**
     * Reset state
     */
    reset(): void {
        this.state = getDefaultStateData<TAnswer>();

        this.emit(createEvent('state:reset', this.contentId, { reset: true }));
    }

    /**
     * Reset for retry (keep some state)
     */
    resetForRetry(): void {
        this.state.state = 'active';
        this.state.selectedAnswer = null;
        this.state.showFeedback = false;
        this.state.isCorrect = null;
        this.state.feedback = undefined;
        this.state.isLocked = false;
    }

    /**
     * Subscribe to events
     */
    on<T = unknown>(
        type:
            | 'state:change'
            | 'state:reset'
            | 'answer:change'
            | 'score:update'
            | 'feedback:show'
            | 'feedback:hide',
        listener: EventListener<T>
    ): EventUnsubscribe {
        return this.emitter.on(type, listener);
    }

    /**
     * Subscribe to all events
     */
    onAny<T = unknown>(listener: EventListener<T>): EventUnsubscribe {
        return this.emitter.onAny(listener);
    }

    /**
     * Emit event
     */
    private emit<T = unknown>(event: EngineEvent<T>): void {
        this.emitter.emit(event);
    }

    /**
     * Cleanup
     */
    destroy(): void {
        this.emitter.removeAllListeners();
    }
}

/**
 * Create a state manager
 */
export function createStateManager<TAnswer = unknown>(
    contentId: string,
    initialState?: Partial<ContentStateData<TAnswer>>
): StateManager<TAnswer> {
    return new StateManager<TAnswer>(contentId, initialState);
}
